import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Modal, Form, Card, Alert, Spinner } from 'react-bootstrap';
import api from '../../api/axios'; // Use centralized api instance
import AuthContext from '../../context/AuthContext';
import { getRoleLabel } from '../../utils/permissions';
import { formatDateTime } from '../../utils/format';

const EMPTY_FORM = { username: '', owner_name: '', password: '', role: 'employee' };

const ManageUsersPage = () => {
    const { user } = useContext(AuthContext); // eslint-disable-line no-unused-vars
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // For Modal — 생성/수정 겸용
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [editingUserId, setEditingUserId] = useState(null);
    const [modalError, setModalError] = useState('');
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/users');
            setUsers(response.data.data);
        } catch (err) {
            setError('직원 목록을 불러오는 데 실패했습니다.');
            console.error("Error fetching users:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleShowCreateModal = () => {
        setModalError('');
        setModalMode('create');
        setEditingUserId(null);
        setForm({ ...EMPTY_FORM });
        setShowModal(true);
    };

    const handleShowEditModal = (u) => {
        setModalError('');
        setModalMode('edit');
        setEditingUserId(u.id);
        // 아이디는 수정 모드에서 읽기전용. 비밀번호는 빈칸 = 변경 안 함.
        setForm({ username: u.username, owner_name: u.owner_name || '', password: '', role: 'employee' });
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleSave = async () => {
        setModalError('');

        // --- 생성 ---
        if (modalMode === 'create') {
            if (!form.username || !form.owner_name || !form.password) {
                setModalError('아이디, 닉네임, 비밀번호를 모두 입력해주세요.');
                return;
            }
            if (form.password.length < 8) {
                setModalError('비밀번호는 8자 이상이어야 합니다.');
                return;
            }
            try {
                await api.post('/users', form);
                fetchUsers();
                handleCloseModal();
            } catch (err) {
                const errorMessage = err.response?.data?.errors?.[0]?.msg || '직원 생성에 실패했습니다.';
                setModalError(errorMessage);
                console.error("Error creating user:", err);
            }
            return;
        }

        // --- 수정 --- 닉네임 필수, 비밀번호는 입력 시에만 변경
        if (!form.owner_name) {
            setModalError('닉네임을 입력해주세요.');
            return;
        }
        if (form.password && form.password.length < 8) {
            setModalError('비밀번호는 8자 이상이어야 합니다. (변경하지 않으려면 비워두세요.)');
            return;
        }
        const payload = { owner_name: form.owner_name };
        if (form.password) payload.password = form.password; // 빈칸이면 비밀번호 미변경
        try {
            await api.put(`/users/${editingUserId}`, payload);
            fetchUsers();
            handleCloseModal();
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.msg || '직원 정보 수정에 실패했습니다.';
            setModalError(errorMessage);
            console.error("Error updating user:", err);
        }
    };

    const handleToggleStatus = async (userToToggle) => {
        const action = userToToggle.isActive ? '비활성화' : '활성화';
        if (window.confirm(`정말로 '${userToToggle.username}' 계정을 ${action}하시겠습니까?`)) {
            try {
                await api.put(`/users/${userToToggle.id}`, { isActive: !userToToggle.isActive });
                fetchUsers(); // Refresh the list
            } catch (err) {
                const errorMessage = err.response?.data?.errors?.[0]?.msg || `계정 ${action}에 실패했습니다.`;
                setError(errorMessage);
                console.error("Error toggling user status:", err);
            }
        }
    };

    const handleChange = (e) => {
        setModalError('');
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    if (loading) return <Spinner animation="border" />;

    const isEdit = modalMode === 'edit';

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>직원 계정</h3>
                    <p>직원 계정과 권한을 관리합니다</p>
                </div>
                <div className="page-header-actions">
                    <Button variant="primary" size="sm" onClick={handleShowCreateModal}>
                        새 직원 추가
                    </Button>
                </div>
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
            <Card>
                <Card.Body>
                    <Table hover responsive>
                        <thead>
                            <tr>
                                <th>아이디</th>
                                <th>닉네임</th>
                                <th>역할</th>
                                <th>상태</th>
                                <th>생성일</th>
                                <th>작업</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id}>
                                    <td>{u.username}</td>
                                    <td>{u.owner_name || <span className="text-muted">-</span>}</td>
                                    <td>{getRoleLabel(u.role)}</td>
                                    <td>{u.isActive ? <span className="text-success">활성</span> : <span className="text-danger">비활성</span>}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(u.created_at)}</td>
                                    <td>
                                        {/* employee 계정만 수정/상태변경 (admin 본인·타admin은 백엔드에서도 403 차단) */}
                                        {u.role === 'employee' && (
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => handleShowEditModal(u)}
                                                >
                                                    수정
                                                </Button>
                                                <Button
                                                    variant={u.isActive ? 'outline-danger' : 'outline-success'}
                                                    size="sm"
                                                    onClick={() => handleToggleStatus(u)}
                                                >
                                                    {u.isActive ? '비활성화' : '활성화'}
                                                </Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? '직원 정보 수정' : '새 직원 계정 생성'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalError && <Alert variant="danger">{modalError}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>아이디</Form.Label>
                            <Form.Control
                                type="text"
                                name="username"
                                value={form.username}
                                onChange={handleChange}
                                disabled={isEdit}
                                readOnly={isEdit}
                            />
                            <Form.Text className="text-muted">
                                {isEdit ? '아이디는 변경할 수 없습니다.' : '로그인에 사용하는 아이디입니다.'}
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>닉네임</Form.Label>
                            <Form.Control type="text" name="owner_name" value={form.owner_name} onChange={handleChange} />
                            <Form.Text className="text-muted">
                                워크보드·담당자 등 화면에 표시되는 이름입니다.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{isEdit ? '새 비밀번호' : '초기 비밀번호'}</Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                value={form.password}
                                onChange={handleChange}
                                placeholder={isEdit ? '변경하지 않으려면 비워두세요' : ''}
                                autoComplete="new-password"
                            />
                            <Form.Text className="text-muted">
                                {isEdit
                                    ? '비워두면 기존 비밀번호가 유지됩니다. 변경 시 8자 이상.'
                                    : '비밀번호는 8자 이상이어야 합니다.'}
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>닫기</Button>
                    <Button variant="primary" onClick={handleSave}>{isEdit ? '저장' : '생성'}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ManageUsersPage;
